import { Search, ClipboardList, BarChart3, Users, ChevronDown, Calendar, Settings, LayoutDashboard, FileText, Sparkles, X, GraduationCap, Wallet, Gauge, Target, Goal } from "lucide-react";
import SidebarSearch from "@/components/SidebarSearch";
import { useCurrentOrganization } from "@/context/OrganizationContext";
import { useSidebar } from "@/context/SidebarContext";
import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

export default function Sidebar() {
  const { isExpanded, setIsExpanded, isMobileOpen, closeMobile } = useSidebar();
  const [expandedMenus, setExpandedMenus] = useState<string[]>([]);
  const [isScrolled, setIsScrolled] = useState(false);
  const [headerHeight, setHeaderHeight] = useState(64);
  const [demoMode, setDemoMode] = useState<boolean>(() => {
    if (typeof window === "undefined") return true;
    // Chaque nouvelle ouverture de l'app démarre en mode démo.
    // Le choix de l'utilisateur n'est conservé que pour la session en cours.
    try {
      const sessionValue = window.sessionStorage.getItem("amelio.demoMode");
      if (sessionValue !== null) return sessionValue === "1";
      window.sessionStorage.setItem("amelio.demoMode", "1");
      window.localStorage.setItem("amelio.demoMode", "1");
    } catch { /* noop */ }
    return true;
  });
  useEffect(() => {
    try {
      const v = demoMode ? "1" : "0";
      window.localStorage.setItem("amelio.demoMode", v);
      window.sessionStorage.setItem("amelio.demoMode", v);
    } catch { /* noop */ }
  }, [demoMode]);
  const location = useLocation();
  const { t } = useTranslation();
  const { orgName } = useCurrentOrganization();

  // Close mobile sidebar on route change
  useEffect(() => {
    closeMobile();
  }, [location.pathname, closeMobile]);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Track header height with ResizeObserver. Re-runs on every route change so
  // the sidebar picks up the header even when it appears later (e.g. when
  // navigating from a demo route — which has no <header> — back to a regular
  // page where AppLayout renders <Header />).
  useEffect(() => {
    let observer: ResizeObserver | null = null;

    const attach = (header: HTMLElement) => {
      observer = new ResizeObserver(([entry]) => {
        setHeaderHeight(entry.contentRect.height);
      });
      observer.observe(header);
      setHeaderHeight(header.offsetHeight);
    };

    const existing = document.querySelector("header[data-app-header]");
    if (existing instanceof HTMLElement) {
      attach(existing);
    } else {
      // No app header yet — wait for it (e.g. when leaving a demo route).
      // Falls back to the default 64px until then so the sidebar stays put
      // and never latches onto an unrelated <header> inside the page content.
      const mo = new MutationObserver(() => {
        const h = document.querySelector("header[data-app-header]");
        if (h instanceof HTMLElement) {
          mo.disconnect();
          attach(h);
        }
      });
      mo.observe(document.body, { childList: true, subtree: true });
      return () => {
        mo.disconnect();
        observer?.disconnect();
      };
    }

    return () => observer?.disconnect();
  }, [location.pathname]);

  useEffect(() => {
    if (location.pathname.startsWith("/administration")) {
      setExpandedMenus((prev) =>
        prev.includes("administration") ? prev : [...prev, "administration"]
      );
    }
    if (location.pathname.startsWith("/rapports")) {
      setExpandedMenus((prev) =>
        prev.includes("rapports") ? prev : [...prev, "rapports"]
      );
    }
    if (location.pathname.startsWith("/employee-portal")) {
      setExpandedMenus((prev) =>
        prev.includes("employee-portal") ? prev : [...prev, "employee-portal"]
      );
    }
    if (location.pathname.startsWith("/mentorat")) {
      setExpandedMenus((prev) =>
        prev.includes("mentoring") ? prev : [...prev, "mentoring"]
      );
    }
    if (
      location.pathname.startsWith("/engagement-dashboard") ||
      location.pathname.startsWith("/action-plan") ||
      location.pathname.startsWith("/your-rituals") ||
      location.pathname.startsWith("/action-library")
    ) {
      setExpandedMenus((prev) =>
        prev.includes("engagement") ? prev : [...prev, "engagement"]
      );
    }
  }, [location.pathname]);

  const isActive = (href: string): boolean => {
    return location.pathname === href || location.pathname.startsWith(href + "/");
  };

  const allNavItems = [
    // { icon: ClipboardList, label: t('sidebar.dashboard'), href: "/evaluation" },
    { icon: Sparkles, label: t('sidebar.intro'), href: "/intro" },
    { icon: Sparkles, label: t('sidebar.demo'), href: "/demo-v3" },
    { icon: Calendar, label: t('sidebar.employeeMeetings'), href: "/rencontres-employes" },
    { icon: ClipboardList, label: t('sidebar.evaluations360', 'Évaluations 360°'), href: "/evaluations-360" },
    { icon: Users, label: t('sidebar.employees'), href: "/employes" },
    { icon: Goal, label: t('sidebar.objectives'), href: "/objectifs" },
    { icon: Target, label: t('sidebar.focus'), href: "/focus" },
    { icon: FileText, label: t('sidebar.questionnaire'), href: "/questionnaire", strikethrough: true, hideInDemo: true },
    { icon: Wallet, label: t('sidebar.compensation'), href: "/remuneration", hideInDemo: true },
  ];
  const navItems = allNavItems.filter((it) => !(demoMode && it.hideInDemo));
  const showEmployeePortal = !demoMode;
  const showMentoring = !demoMode;

  const engagementMenus = [
    { label: t('sidebar.engagementDashboard', "Dashboard"), href: "/engagement-dashboard" },
    { label: t('sidebar.actionPlan', "Plan d'action"), href: "/action-plan" },
    { label: t('sidebar.yourRituals', "Rituels"), href: "/your-rituals" },
    { label: t('sidebar.actionLibrary', "Bibliothèque d'actions"), href: "/action-library" },
  ];

  const employeePortalMenus = [
    { label: t('sidebar.employeePortal'), href: "/employee-portal" },
    { label: t('sidebar.myDevelopment'), href: "/employee-portal/development" },
  ];

  const rapportsMenus = [
    { label: t('sidebar.competencies'), href: "/rapports/competences" },
    { label: t('sidebar.talentAnalysis'), href: "/rapports/analyse-talents" },
    { label: t('sidebar.calibration', 'Calibration'), href: "/rapports/calibration" },
    { label: t('sidebar.compensationReport', 'Rémunération'), href: "/rapports/remuneration" },
  ];

  const mentoringMenus = [
    { label: t('sidebar.findMentor'), href: "/mentorat/trouver" },
    { label: t('sidebar.mySessions'), href: "/mentorat/sessions" },
    { label: t('sidebar.mentoringStats'), href: "/mentorat/statistiques" },
  ];

  const administrationMenus = [
    { label: t('sidebar.company'), href: "/administration/compagnie" },
    { label: t('sidebar.templates'), href: "/administration/modeles" },
    { label: t('sidebar.competencyProfiles'), href: "/administration/profils-competences" },
    { label: t('sidebar.competenciesAdmin'), href: "/administration/competences" },
    { label: t('sidebar.permissions', 'Permissions'), href: "/administration/permissions" },
    { label: t('sidebar.vision'), href: "/administration/vision" },
    { label: t('sidebar.compensation'), href: "/administration/remuneration" },
    { label: t('sidebar.evaluationCycles'), href: "/administration/evaluation-cycles" },
    { label: t('sidebar.cycles360', "Cycles 360"), href: "/administration/cycles-360", strikethrough: true },
    { label: t('sidebar.emails', "Courriels"), href: "/administration/courriels" },
    { label: t('sidebar.storybook'), href: "/administration/storybook" },
    { label: t('sidebar.modalsCatalog', "Fenêtres modales"), href: "/administration/modales" },
  ];

  const toggleMenu = (id: string) => {
    setExpandedMenus((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id],
    );
  };

  // Sidebar content component to avoid duplication
  const SidebarContent = ({ isMobile = false }: { isMobile?: boolean }) => (
    <div className="pt-3 flex-1 flex flex-col overflow-y-auto overflow-x-hidden">
      {/* Organization name */}
      {(isExpanded || isMobile) && orgName && (
        <div className="px-5 mb-3 transition-opacity duration-300">
          <span className="text-xs font-semibold text-navy/60 uppercase tracking-wider">{orgName}</span>
        </div>
      )}
      {/* Demo mode toggle */}
      {(isExpanded || isMobile) && (
        <div className="px-5 mb-3 flex items-center justify-between">
          <span className="text-xs font-medium text-navy/70">{t('sidebar.demoMode', 'Mode démo')}</span>
          <button
            type="button"
            role="switch"
            aria-checked={demoMode}
            onClick={() => setDemoMode((v) => !v)}
            className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
              demoMode ? "bg-turquoise" : "bg-navy/20"
            }`}
            title={t('sidebar.demoMode', 'Mode démo')}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                demoMode ? "translate-x-4" : "translate-x-0.5"
              }`}
            />
          </button>
        </div>
      )}
      {(isExpanded || isMobile) && (
        <SidebarSearch />
      )}

      {/* Navigation */}
      <nav
        className={`flex flex-col gap-0.5 transition-all duration-300 ${(isExpanded || isMobile) ? "px-3.5" : "px-1.5"}`}
      >
        {navItems.map((item, index) => {
          const Icon = item.icon;
          const itemActive = isActive(item.href);
          const sharedClass = `flex items-center gap-3 rounded-md transition-all duration-300 no-underline ${
            (isExpanded || isMobile) ? "px-3 py-3" : "px-2 py-2.5 w-12 justify-center"
          } ${
            itemActive
              ? "bg-white text-turquoise"
              : "text-navy hover:bg-white/50"
          }`;
          return (
            <Link key={index} to={item.href} className={sharedClass} title={!(isExpanded || isMobile) ? item.label : undefined}>
              <Icon className="w-[18px] h-[18px] flex-shrink-0" strokeWidth={1.5} />
              <span className={`text-sm font-medium whitespace-nowrap transition-all duration-300 overflow-hidden ${
                (isExpanded || isMobile) ? "opacity-100 w-auto" : "opacity-0 w-0"
              } ${item.strikethrough ? "line-through" : ""}`}>
                {item.label}
              </span>
            </Link>
          );
        })}

        {/* Employee Portal Menu */}
        {showEmployeePortal && <div className="transition-all duration-300">
          <button
            onClick={() => toggleMenu("employee-portal")}
            className={`flex items-center gap-3 rounded-md transition-all duration-300 ${
              (isExpanded || isMobile)
                ? "px-3 py-3 w-full"
                : "px-2 py-2.5 w-12 justify-center"
            } ${
              isActive("/employee-portal")
                ? "bg-white text-turquoise"
                : "text-navy hover:bg-white/50"
            }`}
            title={!(isExpanded || isMobile) ? t('sidebar.employeePortal') : undefined}
          >
            <LayoutDashboard
              className="w-[18px] h-[18px] flex-shrink-0"
              strokeWidth={1.5}
            />
            <span
              className={`text-sm font-medium whitespace-nowrap line-through transition-all duration-300 overflow-hidden flex-1 text-left ${
                (isExpanded || isMobile) ? "opacity-100 w-auto" : "opacity-0 w-0"
              }`}
            >
              {t('sidebar.employeePortal')}
            </span>
            {(isExpanded || isMobile) && (
              <ChevronDown
                className={`w-4 h-4 transition-transform duration-300 flex-shrink-0 ${
                  expandedMenus.includes("employee-portal") ? "rotate-180" : ""
                }`}
              />
            )}
          </button>

          {(isExpanded || isMobile) && expandedMenus.includes("employee-portal") && (
            <div className="pl-6 mt-1 flex flex-col gap-1">
              {employeePortalMenus.map((subitem, subindex) => {
                const subitemActive = isActive(subitem.href);
                return (
                  <Link
                    key={subindex}
                    to={subitem.href}
                    className={`flex items-center gap-2 px-3 py-2 rounded-md text-xs font-medium transition-all duration-300 no-underline ${
                      subitemActive
                        ? "bg-white text-turquoise"
                        : "text-navy hover:bg-white/50"
                    }`}
                  >
                    <span className={(subitem as { strikethrough?: boolean }).strikethrough ? "line-through opacity-60" : ""}>{subitem.label}</span>
                  </Link>
                );
              })}
            </div>
          )}
        </div>}

        {/* Rapports Menu */}
        <div className="transition-all duration-300">
          <button
            onClick={() => toggleMenu("rapports")}
            className={`flex items-center gap-3 rounded-md transition-all duration-300 ${
              (isExpanded || isMobile)
                ? "px-3 py-3 w-full"
                : "px-2 py-2.5 w-12 justify-center"
            } text-navy hover:bg-white/50`}
            title={!(isExpanded || isMobile) ? t('sidebar.reports') : undefined}
          >
            <BarChart3
              className="w-[18px] h-[18px] flex-shrink-0"
              strokeWidth={1.5}
            />
            <span
              className={`text-sm font-medium whitespace-nowrap transition-all duration-300 overflow-hidden flex-1 text-left ${
                (isExpanded || isMobile) ? "opacity-100 w-auto" : "opacity-0 w-0"
              }`}
            >
              {t('sidebar.reports')}
            </span>
            {(isExpanded || isMobile) && (
              <ChevronDown
                className={`w-4 h-4 transition-transform duration-300 flex-shrink-0 ${
                  expandedMenus.includes("rapports") ? "rotate-180" : ""
                }`}
              />
            )}
          </button>

          {(isExpanded || isMobile) && expandedMenus.includes("rapports") && (
            <div className="pl-6 mt-1 flex flex-col gap-1">
              {rapportsMenus.map((subitem, subindex) => {
                const subitemActive = isActive(subitem.href);
                return (
                  <Link
                    key={subindex}
                    to={subitem.href}
                    className={`flex items-center gap-2 px-3 py-2 rounded-md text-xs font-medium transition-all duration-300 no-underline ${
                      subitemActive
                        ? "bg-white text-turquoise"
                        : "text-navy hover:bg-white/50"
                    }`}
                  >
                    <span>{subitem.label}</span>
                  </Link>
                );
              })}
            </div>
          )}
        </div>

        {/* Mentoring Menu */}
        {showMentoring && <div className="transition-all duration-300">
          <button
            onClick={() => toggleMenu("mentoring")}
            className={`flex items-center gap-3 rounded-md transition-all duration-300 ${
              (isExpanded || isMobile)
                ? "px-3 py-3 w-full"
                : "px-2 py-2.5 w-12 justify-center"
            } ${
              isActive("/mentorat")
                ? "bg-white text-turquoise"
                : "text-navy hover:bg-white/50"
            }`}
            title={!(isExpanded || isMobile) ? t('sidebar.mentoring') : undefined}
          >
            <GraduationCap
              className="w-[18px] h-[18px] flex-shrink-0"
              strokeWidth={1.5}
            />
            <span
              className={`text-sm font-medium whitespace-nowrap transition-all duration-300 overflow-hidden flex-1 text-left ${
                (isExpanded || isMobile) ? "opacity-100 w-auto" : "opacity-0 w-0"
              }`}
            >
              {t('sidebar.mentoring')}
            </span>
            {(isExpanded || isMobile) && (
              <ChevronDown
                className={`w-4 h-4 transition-transform duration-300 flex-shrink-0 ${
                  expandedMenus.includes("mentoring") ? "rotate-180" : ""
                }`}
              />
            )}
          </button>

          {(isExpanded || isMobile) && expandedMenus.includes("mentoring") && (
            <div className="pl-6 mt-1 flex flex-col gap-1">
              {mentoringMenus.map((subitem, subindex) => {
                const subitemActive = isActive(subitem.href);
                return (
                  <Link
                    key={subindex}
                    to={subitem.href}
                    className={`flex items-center gap-2 px-3 py-2 rounded-md text-xs font-medium transition-all duration-300 no-underline ${
                      subitemActive
                        ? "bg-white text-turquoise"
                        : "text-navy hover:bg-white/50"
                    }`}
                  >
                    <span>{subitem.label}</span>
                  </Link>
                );
              })}
            </div>
          )}
        </div>}

        {/* Engagement Menu */}
        <div className="transition-all duration-300">
          <button
            onClick={() => toggleMenu("engagement")}
            className={`flex items-center gap-3 rounded-md transition-all duration-300 ${
              (isExpanded || isMobile)
                ? "px-3 py-3 w-full"
                : "px-2 py-2.5 w-12 justify-center"
            } ${
              isActive("/engagement-dashboard") || isActive("/your-rituals") || isActive("/action-library")
                ? "bg-white text-turquoise"
                : "text-navy hover:bg-white/50"
            }`}
            title={!(isExpanded || isMobile) ? t('sidebar.engagement', "Engagement") : undefined}
          >
            <Gauge
              className="w-[18px] h-[18px] flex-shrink-0"
              strokeWidth={1.5}
            />
            <span
              className={`text-sm font-medium whitespace-nowrap transition-all duration-300 overflow-hidden flex-1 text-left ${
                (isExpanded || isMobile) ? "opacity-100 w-auto" : "opacity-0 w-0"
              }`}
            >
              {t('sidebar.engagement', "Engagement")}
            </span>
            {(isExpanded || isMobile) && (
              <ChevronDown
                className={`w-4 h-4 transition-transform duration-300 flex-shrink-0 ${
                  expandedMenus.includes("engagement") ? "rotate-180" : ""
                }`}
              />
            )}
          </button>

          {(isExpanded || isMobile) && expandedMenus.includes("engagement") && (
            <div className="pl-6 mt-1 flex flex-col gap-1">
              {engagementMenus.map((subitem, subindex) => {
                const subitemActive = isActive(subitem.href);
                return (
                  <Link
                    key={subindex}
                    to={subitem.href}
                    className={`flex items-center gap-2 px-3 py-2 rounded-md text-xs font-medium transition-all duration-300 no-underline ${
                      subitemActive
                        ? "bg-white text-turquoise"
                        : "text-navy hover:bg-white/50"
                    }`}
                  >
                    <span>{subitem.label}</span>
                  </Link>
                );
              })}
            </div>
          )}
        </div>

        {/* Administration Menu */}
        <div className="transition-all duration-300">
          <button
            onClick={() => toggleMenu("administration")}
            className={`flex items-center gap-3 rounded-md transition-all duration-300 ${
              (isExpanded || isMobile)
                ? "px-3 py-3 w-full"
                : "px-2 py-2.5 w-12 justify-center"
            } text-navy hover:bg-white/50`}
            title={!(isExpanded || isMobile) ? t('sidebar.administration') : undefined}
          >
            <Settings
              className="w-[18px] h-[18px] flex-shrink-0"
              strokeWidth={1.5}
            />
            <span
              className={`text-sm font-medium whitespace-nowrap transition-all duration-300 overflow-hidden flex-1 text-left ${
                (isExpanded || isMobile) ? "opacity-100 w-auto" : "opacity-0 w-0"
              }`}
            >
              {t('sidebar.administration')}
            </span>
            {(isExpanded || isMobile) && (
              <ChevronDown
                className={`w-4 h-4 transition-transform duration-300 flex-shrink-0 ${
                  expandedMenus.includes("administration") ? "rotate-180" : ""
                }`}
              />
            )}
          </button>

          {(isExpanded || isMobile) && expandedMenus.includes("administration") && (
            <div className="pl-6 mt-1 flex flex-col gap-1">
              {administrationMenus.map((subitem, subindex) => {
                const subitemActive = isActive(subitem.href);
                return (
                  <Link
                    key={subindex}
                    to={subitem.href}
                    className={`flex items-center gap-2 px-3 py-2 rounded-md text-xs font-medium transition-all duration-300 no-underline ${
                      subitemActive
                        ? "bg-white text-turquoise"
                        : "text-navy hover:bg-white/50"
                    }`}
                  >
                    <span>{subitem.label}</span>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </nav>
    </div>
  );

  return (
    <>
      {/* Desktop Sidebar */}
      <aside
        className={`hidden md:flex fixed left-6 bottom-6 bg-white/50 backdrop-blur-[10px] border border-[#EEFBFF] rounded-[25px] p-2.5 flex-col transition-all duration-300 ease-in-out ${
          isExpanded ? "w-[215px]" : "w-16"
        } z-40`}
        style={{
          top: `calc(${headerHeight}px + 1.5rem)`,
        }}
        onMouseEnter={() => setIsExpanded(true)}
        onMouseLeave={() => setIsExpanded(false)}
      >
        <SidebarContent />
      </aside>

      {/* Mobile Sidebar Overlay */}
      {isMobileOpen && (
        <div
          className="md:hidden fixed inset-0 bg-black/50 z-50"
          onClick={closeMobile}
        />
      )}

      {/* Mobile Sidebar Drawer */}
      <aside
        className={`md:hidden fixed left-0 top-0 bottom-0 w-[280px] bg-white/95 backdrop-blur-[20px] border-r border-[#EEFBFF] p-4 flex flex-col z-50 transition-transform duration-300 ease-in-out ${
          isMobileOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        {/* Mobile Header */}
        <div className="flex items-center justify-between mb-4 pb-4 border-b border-gray-100">
          <img
            src="https://cdn.builder.io/api/v1/image/assets%2F7598ff8c43334f4ba7f31278d001a715%2F2d1f6eb2615f4760807df50fbba39686?format=webp&width=800"
            alt="Amélio"
            className="w-auto h-6"
          />
          <button
            onClick={closeMobile}
            className="p-2 hover:bg-gray-100 rounded-lg transition-all"
          >
            <X className="w-5 h-5 text-gray-600" />
          </button>
        </div>
        <SidebarContent isMobile={true} />
      </aside>
    </>
  );
}
